import { FormEvent, useEffect, useState } from 'react';
import {
  emailCandidate,
  PIPELINE_STAGES,
  PipelineStage,
} from '../../../../network/ats';
import { useToast } from '../../../../utils/useToast';
import {
  Modal,
  errorMessage,
  fieldClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../Pipeline/Modal';
import { Templates } from './Templates';
import { MESSAGE_TEMPLATES, previewTemplate } from './messageTemplates';

type Props = {
  isOpen: boolean;
  onClose(): void;
  onSent?: () => void;
  candidate: {
    id: string;
    name: string;
    email?: string;
    position?: string;
  } | null;
  /** Template to start from (defaults to the offer). */
  initialTemplateId?: string;
};

/** Emails a candidate from a template (offer, invitation, rejection). */
export const OfferVacancyDialog = ({
  isOpen,
  onClose,
  onSent,
  candidate,
  initialTemplateId = 'offer',
}: Props) => {
  const { toast } = useToast();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [stage, setStage] = useState<PipelineStage | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const apply = (id: string) => {
    const template = MESSAGE_TEMPLATES.find((t) => t.id === id);
    if (!template) return;
    const values = {
      candidateName: candidate?.name,
      jobTitle: candidate?.position,
    };
    setTemplateId(template.id);
    setSubject(previewTemplate(template.subject, values));
    setMessage(previewTemplate(template.body, values));
    setStage(template.stage ?? '');
  };

  useEffect(() => {
    if (isOpen) {
      setError(null);
      apply(initialTemplateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidate?.id, initialTemplateId]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!candidate) return;
    if (!subject.trim() || !message.trim()) {
      setError('A subject and a message are required.');
      return;
    }
    if (/\[[^\]]+\]/.test(message)) {
      setError('Replace the [bracketed] parts of the message before sending.');
      return;
    }
    setIsSending(true);
    setError(null);
    try {
      await emailCandidate(candidate.id, {
        subject: subject.trim(),
        message: message.trim(),
        ...(stage ? { stage } : {}),
      });
      toast({ variant: 'success', title: `Email sent to ${candidate.name}.` });
      onSent?.();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'The email could not be sent.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen && Boolean(candidate)}
      onClose={onClose}
      title={`Email ${candidate?.name ?? 'candidate'}`}
      description={
        candidate?.email
          ? `Sent to ${candidate.email}. {{companyName}} is filled in when sent.`
          : 'This candidate has no email address. Add one by editing the candidate.'
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Templates selectedId={templateId} onSelect={(t) => apply(t.id)} />
        <div>
          <label htmlFor="message-subject" className={labelClass}>
            Subject
          </label>
          <input
            id="message-subject"
            className={fieldClass}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
        </div>
        <div>
          <label htmlFor="message-body" className={labelClass}>
            Message
          </label>
          <textarea
            id="message-body"
            rows={10}
            className={fieldClass}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </div>
        <div>
          <label htmlFor="message-stage" className={labelClass}>
            Then move the candidate to
          </label>
          <select
            id="message-stage"
            className={fieldClass}
            value={stage}
            onChange={(event) => setStage(event.target.value as PipelineStage)}
          >
            <option value="">Keep the current stage</option>
            {PIPELINE_STAGES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className={secondaryButtonClass}
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={primaryButtonClass}
            disabled={isSending || !candidate?.email}
          >
            {isSending ? 'Sending…' : 'Send email'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
